import { Buffer } from "buffer";

import { MedplumClient } from "@medplum/core";

import * as Sentry from "@sentry/react";

import type { CalendarSyncEmailRecipient } from "@/scheduling/types/calendar-sync-email";

export interface IcsEmailAttachment {
  filename: string;
  content: string;
}

interface SendEmailThroughBotProps {
  email: string;
  subject: string;
  organizationName: string;
  htmlContent: string;
  mailgunDomain: string;
  mailgunApiKey: string;
  icsAttachment?: IcsEmailAttachment;
}

interface SendEmailProps {
  medplum: MedplumClient;
  email: string;
  subject: string;
  organizationName: string;
  htmlContent: string;
  appointmentId?: string;
  calendarSyncRecipient?: CalendarSyncEmailRecipient;
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Sends an email directly via the Mailgun API. Intended for use inside bots
 * where Mailgun credentials are available as bot secrets.
 *
 * @param email - Recipient email address
 * @param subject - Email subject line
 * @param organizationName - Display name used as the sender name
 * @param htmlContent - HTML body of the email
 * @param mailgunDomain - Mailgun sending domain (from bot secrets)
 * @param mailgunApiKey - Mailgun API key (from bot secrets)
 * @param icsAttachment - Optional ICS calendar attachment
 * @returns The raw Mailgun API response text
 */
const sendEmailThroughBot = async ({
  email,
  subject,
  organizationName,
  htmlContent,
  mailgunDomain,
  mailgunApiKey,
  icsAttachment,
}: SendEmailThroughBotProps) => {
  if (!isValidEmail(email)) {
    throw new Error(`Invalid email address: ${email}`);
  }

  try {
    const form = new FormData();
    form.append("from", `${organizationName} <postmaster@${mailgunDomain}>`);
    form.append("to", email);
    form.append("subject", subject);
    form.append("html", htmlContent);

    if (icsAttachment) {
      form.append(
        "attachment",
        new Blob([icsAttachment.content], { type: "text/calendar" }),
        icsAttachment.filename,
      );
    }

    const resp = await fetch(`https://api.mailgun.net/v3/${mailgunDomain}/messages`, {
      method: "POST",
      headers: {
        Authorization: `Basic ${Buffer.from(`api:${mailgunApiKey}`).toString("base64")}`,
      },
      body: form,
    });

    if (!resp.ok) {
      const errorText = await resp.text();
      throw new Error(`Mailgun API error: ${resp.status} ${resp.statusText} - ${errorText}`);
    }

    return resp.text();
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

/**
 * Sends an email from frontend or bot code by invoking the emails bot, which holds
 * the Mailgun credentials as secrets. HTML content is base64-encoded for transport.
 *
 * @param medplum - Authenticated Medplum client used to execute the bot
 * @param email - Recipient email address
 * @param subject - Email subject line
 * @param organizationName - Display name used as the sender name
 * @param htmlContent - HTML body of the email
 * @param appointmentId - Optional appointment id for ICS attachment generation
 * @param calendarSyncRecipient - Recipient role used to evaluate calendar sync consent
 */
const sendEmail = async ({
  medplum,
  email,
  subject,
  organizationName,
  htmlContent,
  appointmentId,
  calendarSyncRecipient,
}: SendEmailProps): Promise<void> => {
  await medplum.post(medplum.fhirUrl("$qh-send-email"), {
    to: email,
    subject,
    organizationName,
    emailHtml: Buffer.from(htmlContent).toString("base64"),
    ...(appointmentId ? { appointmentId } : {}),
    ...(calendarSyncRecipient ? { calendarSyncRecipient } : {}),
  });
};

export { sendEmailThroughBot, sendEmail };
export type { SendEmailProps, SendEmailThroughBotProps };
